import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { useNavigate } from 'react-router-dom';
import { DataService } from 'src/services/data-service';
import { UserStatus, IUserDetails } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import AddUser from './add-user';
import UpdateUser from './update-user';
import UpdateUserPassword from './update-user-password';

interface IUserListing {
  id: number;
  name: string;
  email: string;
  role: string;
  active: boolean;
  status: string;
}

const UserListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState<Record<string, IUserListing>>();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddUser, setOpenAddUser] = useState<boolean>(false);
  const [openUpdateUser, setOpenUpdateUser] = useState<boolean>(false);
  const [openUpdatePassword, setOpenUpdatePassword] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUserDetails | null>(null);

  const activate = useCallback((id: string) => {
    const confimation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this user?',
      onSubmit: async () => {
        const result = await DataService.post('api/User/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'User activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate user', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confimation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confimation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this user?',
      onSubmit: async () => {
        const result = await DataService.post(
          'api/User/deactivate?id=' + id,
          {}
        );
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'User deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate user', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confimation));
  }, [dispatch]);

  const editUser = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/User/get?id=' + id);
      if (response.ok) {
        const res = await response.json();
        const user: IUserDetails = res.data;
        setSelectedUser(user);
        setOpenUpdateUser(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch user details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading user details', type: 'error' }));
    }
  }, [dispatch]);

  const updatePassword = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/User/get?id=' + id);
      if (response.ok) {
        const res = await response.json();
        const user: IUserDetails = res.data;
        setSelectedUser(user);
        setOpenUpdatePassword(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch user details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading user details', type: 'error' }));
    }
  }, [dispatch]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'name', label: 'Name', type: EnhancedTableColumnType.TEXT },
    { id: 'email', label: 'Email', type: EnhancedTableColumnType.TEXT },
    { id: 'role', label: 'Role', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: editUser,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit User'
        },
        {
          onClick: updatePassword,
          icon: <LockIcon fontSize="small" />,
          label: 'Update Password'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate User',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate User',
          hidden: (r) => r.active === false
        }
      ]
    }
  ], [activate, deactivate, editUser, updatePassword]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/User/all');
      if (response.ok) {
        const res = await response.json();
        const list: IUserDetails[] = res.data;
        const result: Record<string, IUserListing> = {};

        list.forEach((c) => {
          result[c.id] = {
            id: c.id,
            name: c.name,
            email: c.email,
            role: c.role,
            active: c.active,
            status: c.active ? 'Active' : 'Inactive'
          };
        });

        setData(result);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch users', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading users', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddUser(false);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({ message: 'User added successfully!', type: 'success' }));
  }, [dispatch]);

  const handleUpdateSuccess = useCallback(() => {
    setOpenUpdateUser(false);
    setSelectedUser(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: 'User updated successfully!',
      type: 'success'
    }));
  }, [dispatch]);

  const handlePasswordUpdateSuccess = useCallback(() => {
    setOpenUpdatePassword(false);
    setSelectedUser(null);
    dispatch(showAlert({
      message: 'Password updated successfully!',
      type: 'success'
    }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    setOpenUpdateUser(false);
    setOpenUpdatePassword(false);
    setSelectedUser(null);
  }, []);

  const actions: ITableMenuAction[] = [];

  if (!data || loading) {
    return (
      <Box>
        <Helmet>
          <title>User's List</title>
        </Helmet>
        <MainPageTitle title={'Users List'} />
        <Loader />
      </Box>
    );
  }

  return (
    <div>
      <Helmet>
        <title>User's List</title>
      </Helmet>
      <MainPageTitle
        title={'Users List'}
        action={{
          title: 'Add User',
          onClick: () => {
            setOpenAddUser(true);
          }
        }}
      />

      <EnhancedTable
        title="Users"
        columns={headers}
        data={data}
        actions={actions}
      />
      {openAddUser && (
        <AddUser
          onSuccess={handleAddSuccess}
          onClose={() => setOpenAddUser(false)}
        />
      )}

      {openUpdateUser && selectedUser && (
        <UpdateUser
          onSuccess={handleUpdateSuccess}
          onClose={handleClose}
          user={selectedUser}
        />
      )}

      {openUpdatePassword && selectedUser && (
        <UpdateUserPassword
          onSuccess={handlePasswordUpdateSuccess}
          onClose={handleClose}
          userId={selectedUser.id}
        />
      )}
    </div>
  );
};

export default UserListing;